import { useCallback, useEffect, useMemo, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Brain, Check, Eye, Loader2, Pin, PinOff, Plus, Search, Trash2, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PORTEES = {
  global: { label: "Rudolph & règles", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  societe: { label: "Société", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  projet: { label: "Projet", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  agent: { label: "Agent", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};
export const CATEGORIES = {
  regle: "Règle", fait: "Fait", decision: "Décision", preference: "Préférence", contact: "Contact",
};

/**
 * Carte d'un souvenir : valider, épingler, modifier le texte, archiver.
 * Partagée avec l'onglet « Activité IA » des projets.
 */
export function Souvenir({ m, cibleLabel, onMaj, onSupprimer }) {
  const [edition, setEdition] = useState(false);
  const [texte, setTexte] = useState(m.texte);
  const aValider = m.statut === "a_valider";
  return (
    <div className={`rounded-xl border bg-white p-3 ${aValider ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className={`rounded-full border px-2 py-0.5 font-medium ${PORTEES[m.portee]?.cls}`}>
          {PORTEES[m.portee]?.label}{cibleLabel ? ` · ${cibleLabel}` : ""}
        </span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{CATEGORIES[m.categorie] || m.categorie}</span>
        {aValider && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">Proposé par un agent</span>}
        {m.epingle && <Pin className="h-3.5 w-3.5 text-indigo-500" />}
        <span className="ml-auto text-slate-400">{m.source}</span>
      </div>
      {edition ? (
        <div className="mt-2 flex gap-2">
          <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={2}
            className="flex-1 rounded-lg border border-slate-300 p-2 text-sm" />
          <div className="flex flex-col gap-1">
            <button className="rounded-lg bg-indigo-600 p-1.5 text-white" title="Enregistrer"
              onClick={() => { onMaj(m.id, { texte }); setEdition(false); }}><Check className="h-4 w-4" /></button>
            <button className="rounded-lg border p-1.5" title="Annuler"
              onClick={() => { setTexte(m.texte); setEdition(false); }}><X className="h-4 w-4" /></button>
          </div>
        </div>
      ) : (
        <p className="mt-2 cursor-text whitespace-pre-wrap text-sm text-slate-800" onClick={() => setEdition(true)}
          title="Cliquer pour modifier">{m.texte}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {aValider && (
          <button onClick={() => onMaj(m.id, { statut: "actif" })}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white">
            <Check className="h-3.5 w-3.5" /> Retenir
          </button>
        )}
        <button onClick={() => onMaj(m.id, { epingle: !m.epingle })}
          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">
          {m.epingle ? <><PinOff className="h-3.5 w-3.5" /> Désépingler</> : <><Pin className="h-3.5 w-3.5" /> Épingler</>}
        </button>
        <button onClick={() => onSupprimer(m.id)}
          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs text-red-600 hover:bg-red-50">
          <Trash2 className="h-3.5 w-3.5" /> {aValider ? "Rejeter" : "Oublier"}
        </button>
      </div>
    </div>
  );
}

/** Formulaire d'ajout d'un souvenir (portée imposée possible). */
export function AjoutSouvenir({ token, portee: porteeFixe, cible: cibleFixe, projets = [], agents = [], onAjout }) {
  const [texte, setTexte] = useState("");
  const [portee, setPortee] = useState(porteeFixe || "global");
  const [cible, setCible] = useState(cibleFixe || "");
  const [categorie, setCategorie] = useState("fait");
  const [envoi, setEnvoi] = useState(false);

  const ajouter = async () => {
    if (!texte.trim()) return;
    setEnvoi(true);
    try {
      const r = await fetch(`${API_URL}/api/agent/memoire`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ texte, portee, cible: portee === "global" ? null : cible || null, categorie }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erreur");
      setTexte("");
      toast.success("Retenu : tous les agents le sauront");
      onAjout?.(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
      <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={2}
        placeholder="Un fait, une règle ou une décision à ne plus jamais rappeler…"
        className="w-full rounded-lg border border-slate-300 bg-white p-2 text-sm" />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!porteeFixe && (
          <select value={portee} onChange={(e) => { setPortee(e.target.value); setCible(""); }}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            {Object.entries(PORTEES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        )}
        {!porteeFixe && portee === "societe" && (
          <input value={cible} onChange={(e) => setCible(e.target.value)} placeholder="Société (ex. RM Location)"
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm" />
        )}
        {!porteeFixe && portee === "projet" && (
          <select value={cible} onChange={(e) => setCible(e.target.value)}
            className="max-w-[16rem] rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            <option value="">Projet…</option>
            {projets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {!porteeFixe && portee === "agent" && (
          <select value={cible} onChange={(e) => setCible(e.target.value)}
            className="max-w-[16rem] rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            <option value="">Agent…</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <select value={categorie} onChange={(e) => setCategorie(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
          {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={ajouter} disabled={envoi || !texte.trim()}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
          {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Retenir
        </button>
      </div>
    </div>
  );
}

export function useMemoire(token, filtres = {}) {
  const [elements, setElements] = useState(null);
  const cle = JSON.stringify(filtres);
  const charger = useCallback(async () => {
    const params = new URLSearchParams(Object.entries(JSON.parse(cle)).filter(([, v]) => v));
    const r = await fetch(`${API_URL}/api/agent/memoire?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    setElements(r.ok ? await r.json() : []);
  }, [token, cle]);
  useEffect(() => { charger(); }, [charger]);
  const maj = async (id, champs) => {
    const r = await fetch(`${API_URL}/api/agent/memoire/${id}`, {
      method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(champs),
    });
    if (!r.ok) toast.error("Modification impossible");
    charger();
  };
  const supprimer = async (id) => {
    await fetch(`${API_URL}/api/agent/memoire/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    charger();
  };
  return { elements, charger, maj, supprimer };
}

export default function MemoirePage() {
  const { token } = useAuth();
  const [filtre, setFiltre] = useState("tout");
  const [q, setQ] = useState("");
  const [projets, setProjets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [apercu, setApercu] = useState(null);
  const { elements, charger, maj, supprimer } = useMemoire(token, { q });

  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    fetch(`${API_URL}/api/projects`, h).then((r) => r.json()).then((d) => setProjets(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_URL}/api/atelier/agents`, h).then((r) => r.json()).then((d) => setAgents(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const libelleCible = useCallback((m) => {
    if (m.portee === "projet") return projets.find((p) => p.id === m.cible)?.name || "projet";
    if (m.portee === "agent") return agents.find((a) => a.id === m.cible)?.name || "agent";
    return m.cible;
  }, [projets, agents]);

  const aValider = (elements || []).filter((m) => m.statut === "a_valider");
  const visibles = useMemo(() => (elements || []).filter((m) => m.statut !== "a_valider"
    && (filtre === "tout" || m.portee === filtre)), [elements, filtre]);

  const voirApercu = async () => {
    const r = await fetch(`${API_URL}/api/agent/memoire/apercu`, { headers: { Authorization: `Bearer ${token}` } });
    setApercu((await r.json()).contexte || "Mémoire vide.");
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <Brain className="h-6 w-6 text-indigo-600" /> Mémoire commune
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Ce que tous les agents savent déjà : l'assistant NovaForge, les agents, les équipes IA des projets
              et les sessions Orca / GitHub la lisent au démarrage. Tu n'as plus rien à rappeler.
            </p>
          </div>
          <button onClick={voirApercu}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <Eye className="h-4 w-4" /> Ce que lit un agent
          </button>
        </div>

        <div className="mt-5"><AjoutSouvenir token={token} projets={projets} agents={agents} onAjout={charger} /></div>

        {aValider.length > 0 && (
          <div className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-amber-700">
              À valider · {aValider.length} proposé{aValider.length > 1 ? "s" : ""} par les agents
            </h2>
            <div className="mt-2 grid gap-2 lg:grid-cols-2">
              {aValider.map((m) => <Souvenir key={m.id} m={m} cibleLabel={libelleCible(m)} onMaj={maj} onSupprimer={supprimer} />)}
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {[["tout", "Tout"], ...Object.entries(PORTEES).map(([k, v]) => [k, v.label])].map(([k, l]) => (
            <button key={k} onClick={() => setFiltre(k)}
              className={`rounded-full px-3 py-1 text-sm ${filtre === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
              {l}
            </button>
          ))}
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher"
              className="rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm" />
          </div>
        </div>

        {!elements ? <Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="mt-3 grid gap-2 lg:grid-cols-2">
            {visibles.map((m) => <Souvenir key={m.id} m={m} cibleLabel={libelleCible(m)} onMaj={maj} onSupprimer={supprimer} />)}
            {!visibles.length && <p className="text-sm text-slate-500">Rien ici pour l'instant.</p>}
          </div>
        )}
      </div>

      {apercu && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setApercu(null)}>
          <aside className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Ce qu'un agent lit au démarrage</h2>
              <button onClick={() => setApercu(null)} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <pre className="mt-4 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-xs text-slate-700">{apercu}</pre>
          </aside>
        </div>
      )}
    </CRMLayout>
  );
}
